import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { getDatePlusMinus, getUnixTime } from '@einhaus/meteoric-helpers';
import type { BarDuration, ChartMarker, OBVAnalyzerEvent } from '@einhaus/project314-types';
import AdminLayout from '@/components/Layout/AdminLayout';
import Alert from '@/components/UI/Alert';
import Card from '@/components/UI/Card';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { showToast } from '@/components/Layout/Toast';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import AiAuditCard from './components/AiAuditCard';
import AuditFiltersCard from './components/AuditFiltersCard';
import AuditSummaryCards from './components/AuditSummaryCards';
import EventDistributionCard from './components/EventDistributionCard';
import FilteredEventTimelineCard from './components/FilteredEventTimelineCard';
import FocusedDailySnapshotCard from './components/FocusedDailySnapshotCard';
import FocusedEventsCard from './components/FocusedEventsCard';
import FocusedRuntimeStateCard from './components/FocusedRuntimeStateCard';
import ObvLineSignalCard from './components/ObvLineSignalCard';
import PriceChartCard from './components/PriceChartCard';
import ReviewerChecklistCard from './components/ReviewerChecklistCard';
import {
    ALL_OBV_EVENT_TYPES,
    DEFAULT_AFTER_HOURS_REMOVAL,
    DEFAULT_DURATION,
    DEFAULT_END_DATE_TIME,
    DEFAULT_FOCUSED_MARKER_CONTEXT_BARS,
    DEFAULT_FOCUSED_MARKERS_ONLY,
    DEFAULT_SYMBOL,
    DEFAULT_WINDOW_DAYS
} from './components/constants';
import {
    applySelectedEventTypesToSearchParams,
    buildCompactMarkerText,
    buildObvAuditRenderUrl,
    getEventMarkerPosition,
    getEventTone,
    getFocusedChartWindow,
    getFormStringValue,
    getToneMarkerLabel,
    isAuditDuration,
    parseBooleanSearchParam,
    parsePositiveInteger,
    parseSelectedEventTypes
} from './components/helpers';
import type { AuditPreset, ObvAiAuditResult, ObvAuditStateHistoryRow, RouterInputs, RuntimeStateRow } from './components/types';

const OBVAudit = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [latestAiAuditResultState, setLatestAiAuditResultState] = useState<{
        result: ObvAiAuditResult;
        reviewKey: string;
    } | null>(null);

    const resolvedSymbol = (searchParams.get('symbol') ?? DEFAULT_SYMBOL).trim().toUpperCase();
    const durationSearchParam = searchParams.get('duration');
    const resolvedDuration: BarDuration = isAuditDuration(durationSearchParam) ? durationSearchParam : DEFAULT_DURATION;
    const resolvedEndDateTime = searchParams.get('endDateTime') ?? DEFAULT_END_DATE_TIME;
    const resolvedFocusDateTime = searchParams.get('focusDateTime');
    const resolvedWindowDays = parsePositiveInteger(searchParams.get('windowDays'), DEFAULT_WINDOW_DAYS);
    const resolvedShouldRemoveAfterHours = parseBooleanSearchParam(searchParams.get('shouldRemoveAfterHours'), DEFAULT_AFTER_HOURS_REMOVAL);
    const resolvedSelectedEventTypes = parseSelectedEventTypes(searchParams.get('eventTypes'));

    const resolvedShowFocusedMarkersOnly = parseBooleanSearchParam(
        searchParams.get('showFocusedMarkersOnly'),
        DEFAULT_FOCUSED_MARKERS_ONLY
    );

    const currentReviewKey = searchParams.toString();

    const auditQueryStartDateTime = useMemo(
        () => getDatePlusMinus('-', resolvedWindowDays, 'day', 'ymdhms', 'start', resolvedEndDateTime),
        [resolvedEndDateTime, resolvedWindowDays]
    );

    const chartWindow = useMemo(
        () =>
            getFocusedChartWindow({
                duration: resolvedDuration,
                fallbackEndDateTime: resolvedEndDateTime,
                focusDateTime: resolvedFocusDateTime,
                windowDays: resolvedWindowDays
            }),
        [resolvedDuration, resolvedEndDateTime, resolvedFocusDateTime, resolvedWindowDays]
    );

    const symbolSearchQuery = useAppQuery({
        ...trpc.search.symbolSearch.queryOptions({
            term: resolvedSymbol
        }),
        enabled: resolvedSymbol.length > 0
    });

    const resolvedAssetSearchResult =
        symbolSearchQuery.data?.results.find((result) => result.entityType === 'Asset' && result.symbol.toUpperCase() === resolvedSymbol) ??
        null;

    const entityQuery = useAppQuery({
        ...trpc.entity.details.queryOptions({
            entityId: resolvedAssetSearchResult?.entityId ?? 0,
            entityType: 'Asset'
        }),
        enabled: resolvedAssetSearchResult !== null
    });

    const entity = entityQuery.data?.entity ?? null;

    const auditQuery = useAppQuery({
        ...trpc.entity.fetchObvAudit.queryOptions({
            entityId: entity?.id ?? 0,
            entityType: 'Asset',
            startDateTime: auditQueryStartDateTime,
            endDateTime: resolvedEndDateTime,
            duration: resolvedDuration,
            focusDateTime: resolvedFocusDateTime ?? undefined,
            shouldRemoveAfterHours: resolvedShouldRemoveAfterHours
        }),
        enabled: entity !== null
    });

    const sortedEvents = useMemo(
        () => [...(auditQuery.data?.events ?? [])].sort((a, b) => b.event.timestamp - a.event.timestamp),
        [auditQuery.data?.events]
    );

    const stateHistoryByDateTime = useMemo(() => {
        const stateMap = new Map<string, ObvAuditStateHistoryRow>();

        for (const stateRow of auditQuery.data?.stateHistory ?? []) {
            stateMap.set(stateRow.dateTime, stateRow);
        }

        return stateMap;
    }, [auditQuery.data?.stateHistory]);

    const filteredEvents = useMemo(
        () => sortedEvents.filter((eventRow) => resolvedSelectedEventTypes.has(eventRow.event.type)),
        [resolvedSelectedEventTypes, sortedEvents]
    );

    const selectedDateTime = resolvedFocusDateTime ?? filteredEvents[0]?.dateTime ?? auditQuery.data?.latestState?.dateTime ?? null;
    const selectedStateHistoryRow = selectedDateTime ? (stateHistoryByDateTime.get(selectedDateTime) ?? null) : null;

    const selectedState: RuntimeStateRow | null =
        auditQuery.data?.focusedState ??
        (selectedStateHistoryRow
            ? {
                  dateTime: selectedStateHistoryRow.dateTime,
                  state: selectedStateHistoryRow.state,
                  timestamp: selectedStateHistoryRow.timestamp
              }
            : auditQuery.data?.latestState
              ? {
                    dateTime: auditQuery.data.latestState.dateTime,
                    state: auditQuery.data.latestState.state,
                    timestamp: auditQuery.data.latestState.timestamp
                }
              : null);

    const selectedEvents = useMemo(
        () => (selectedDateTime ? filteredEvents.filter((eventRow) => eventRow.dateTime === selectedDateTime) : []),
        [filteredEvents, selectedDateTime]
    );

    const selectedEventMarkerLabelByKey = useMemo(() => {
        const markerLabelMap = new Map<string, string>();
        const toneOrdinalByTone = new Map<'bullish' | 'bearish' | 'neutral', number>();

        for (const eventRow of selectedEvents) {
            const eventTone = getEventTone(eventRow.event);
            const nextOrdinal = (toneOrdinalByTone.get(eventTone) ?? 0) + 1;
            toneOrdinalByTone.set(eventTone, nextOrdinal);

            markerLabelMap.set(
                `${eventRow.dateTime}:${eventRow.event.type}:${eventRow.event.barIndex}`,
                `${getToneMarkerLabel(eventTone)}${nextOrdinal}`
            );
        }

        return markerLabelMap;
    }, [selectedEvents]);

    const filteredEventDateTimes = useMemo(() => {
        const orderedDateTimes: string[] = [];
        const seenDateTimes = new Set<string>();

        for (const eventRow of filteredEvents) {
            if (seenDateTimes.has(eventRow.dateTime)) {
                continue;
            }

            seenDateTimes.add(eventRow.dateTime);
            orderedDateTimes.push(eventRow.dateTime);
        }

        return orderedDateTimes;
    }, [filteredEvents]);

    const selectedEventDateTimeIndex = selectedDateTime ? filteredEventDateTimes.indexOf(selectedDateTime) : -1;

    const chartWindowTimestampRange = useMemo(
        () => ({
            startTimestamp: getUnixTime(chartWindow.startDateTime),
            endTimestamp: getUnixTime(chartWindow.endDateTime)
        }),
        [chartWindow.endDateTime, chartWindow.startDateTime]
    );

    const visibleFilteredEvents = useMemo(
        () =>
            filteredEvents.filter(
                (eventRow) =>
                    eventRow.event.timestamp >= chartWindowTimestampRange.startTimestamp &&
                    eventRow.event.timestamp <= chartWindowTimestampRange.endTimestamp
            ),
        [chartWindowTimestampRange.endTimestamp, chartWindowTimestampRange.startTimestamp, filteredEvents]
    );

    const visibleStateHistory = useMemo(
        () =>
            (auditQuery.data?.stateHistory ?? []).filter(
                (stateRow) =>
                    stateRow.timestamp >= chartWindowTimestampRange.startTimestamp &&
                    stateRow.timestamp <= chartWindowTimestampRange.endTimestamp
            ),
        [auditQuery.data?.stateHistory, chartWindowTimestampRange.endTimestamp, chartWindowTimestampRange.startTimestamp]
    );

    const visibleStateIndexByDateTime = useMemo(() => {
        const indexMap = new Map<string, number>();

        for (const [index, stateRow] of visibleStateHistory.entries()) {
            indexMap.set(stateRow.dateTime, index);
        }

        return indexMap;
    }, [visibleStateHistory]);

    const chartMarkerEvents = useMemo(() => {
        if (!resolvedShowFocusedMarkersOnly || !selectedDateTime) {
            return visibleFilteredEvents;
        }

        const selectedIndex = visibleStateIndexByDateTime.get(selectedDateTime);

        if (selectedIndex === undefined) {
            return visibleFilteredEvents.filter((eventRow) => eventRow.dateTime === selectedDateTime);
        }

        const groupedEventsByDateTime = new Map<
            string,
            {
                distanceFromFocus: number;
                rows: typeof visibleFilteredEvents;
            }
        >();

        for (const eventRow of visibleFilteredEvents) {
            const eventIndex = visibleStateIndexByDateTime.get(eventRow.dateTime);

            if (eventIndex === undefined) {
                continue;
            }

            const existingGroup = groupedEventsByDateTime.get(eventRow.dateTime);

            if (existingGroup) {
                existingGroup.rows.push(eventRow);
                continue;
            }

            groupedEventsByDateTime.set(eventRow.dateTime, {
                distanceFromFocus: Math.abs(eventIndex - selectedIndex),
                rows: [eventRow]
            });
        }

        return [...groupedEventsByDateTime.values()]
            .sort((a, b) => {
                if (a.distanceFromFocus !== b.distanceFromFocus) {
                    return a.distanceFromFocus - b.distanceFromFocus;
                }

                const firstTimestamp = a.rows[0]?.event.timestamp ?? 0;
                const secondTimestamp = b.rows[0]?.event.timestamp ?? 0;
                return firstTimestamp - secondTimestamp;
            })
            .slice(0, DEFAULT_FOCUSED_MARKER_CONTEXT_BARS)
            .flatMap((group) => group.rows)
            .sort((a, b) => a.event.timestamp - b.event.timestamp);
    }, [resolvedShowFocusedMarkersOnly, selectedDateTime, visibleFilteredEvents, visibleStateIndexByDateTime]);

    const markers = useMemo<ChartMarker[]>(
        () =>
            chartMarkerEvents.map((eventRow) => ({
                dateTime: eventRow.dateTime,
                position: getEventMarkerPosition(eventRow.event),
                text:
                    eventRow.dateTime === selectedDateTime
                        ? (selectedEventMarkerLabelByKey.get(`${eventRow.dateTime}:${eventRow.event.type}:${eventRow.event.barIndex}`) ??
                          buildCompactMarkerText(eventRow.event))
                        : resolvedShowFocusedMarkersOnly
                          ? getToneMarkerLabel(getEventTone(eventRow.event))
                          : buildCompactMarkerText(eventRow.event)
            })),
        [chartMarkerEvents, resolvedShowFocusedMarkersOnly, selectedDateTime, selectedEventMarkerLabelByKey]
    );

    const obvChartPoints = useMemo(
        () =>
            visibleStateHistory.map((stateRow) => ({
                dateTime: stateRow.dateTime,
                signal: stateRow.state.line.signal,
                timestamp: stateRow.timestamp,
                value: stateRow.state.line.value
            })),
        [visibleStateHistory]
    );

    const obvChartMarkers = useMemo(
        () =>
            chartMarkerEvents.map((eventRow) => ({
                dateTime: eventRow.dateTime,
                position: getEventMarkerPosition(eventRow.event),
                text:
                    eventRow.dateTime === selectedDateTime
                        ? (selectedEventMarkerLabelByKey.get(`${eventRow.dateTime}:${eventRow.event.type}:${eventRow.event.barIndex}`) ??
                          buildCompactMarkerText(eventRow.event))
                        : resolvedShowFocusedMarkersOnly
                          ? getToneMarkerLabel(getEventTone(eventRow.event))
                          : buildCompactMarkerText(eventRow.event),
                tone: getEventTone(eventRow.event)
            })),
        [chartMarkerEvents, resolvedShowFocusedMarkersOnly, selectedDateTime, selectedEventMarkerLabelByKey]
    );

    const eventCounts = useMemo(() => {
        const counts = new Map<OBVAnalyzerEvent['type'], number>();

        for (const eventRow of sortedEvents) {
            counts.set(eventRow.event.type, (counts.get(eventRow.event.type) ?? 0) + 1);
        }

        return [...counts.entries()]
            .map(([type, count]) => ({ count, type }))
            .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type));
    }, [sortedEvents]);

    const resolvedSelectedEventTypeList = ALL_OBV_EVENT_TYPES.filter((eventType) => resolvedSelectedEventTypes.has(eventType));

    const renderUrls = entity
        ? {
              focused: buildObvAuditRenderUrl({
                  duration: resolvedDuration,
                  endDateTime: resolvedEndDateTime,
                  entityId: entity.id,
                  focusDateTime: selectedDateTime,
                  selectedEventTypes: resolvedSelectedEventTypeList,
                  shouldRemoveAfterHours: resolvedShouldRemoveAfterHours,
                  variant: 'focused',
                  windowDays: resolvedWindowDays
              }),
              wide: buildObvAuditRenderUrl({
                  duration: resolvedDuration,
                  endDateTime: resolvedEndDateTime,
                  entityId: entity.id,
                  focusDateTime: selectedDateTime,
                  selectedEventTypes: resolvedSelectedEventTypeList,
                  shouldRemoveAfterHours: resolvedShouldRemoveAfterHours,
                  variant: 'wide',
                  windowDays: resolvedWindowDays
              })
          }
        : null;

    const runAiAuditMutation = useMutation({
        ...trpc.entity.runObvAiAudit.mutationOptions({
            onSuccess: ({ result }) => {
                setLatestAiAuditResultState({
                    result,
                    reviewKey: currentReviewKey
                });

                showToast(`OBV AI audit saved using ${result.reviewerModelKey}`, 'success');
            },
            onError: (error) => {
                showToast(error.message || 'Failed to run OBV AI audit', 'error');
            }
        })
    });

    const latestAiAuditResult = latestAiAuditResultState?.reviewKey === currentReviewKey ? latestAiAuditResultState.result : null;

    const handleRunAiAudit = async () => {
        if (!entity) {
            showToast('Load an OBV audit view before running AI review', 'warning');
            return;
        }

        await runAiAuditMutation.mutateAsync({
            entityId: entity.id,
            entityType: 'Asset',
            duration: resolvedDuration,
            endDateTime: resolvedEndDateTime,
            focusDateTime: selectedDateTime,
            selectedEventTypes: resolvedSelectedEventTypeList,
            shouldRemoveAfterHours: resolvedShouldRemoveAfterHours,
            windowDays: resolvedWindowDays
        } satisfies RouterInputs['entity']['runObvAiAudit']);
    };

    const handleApplyFilters = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const rawSymbol = getFormStringValue(formData, 'symbol');
        const rawDuration = getFormStringValue(formData, 'duration');
        const rawEndDateTime = getFormStringValue(formData, 'endDateTime');
        const rawFocusDateTime = getFormStringValue(formData, 'focusDateTime');
        const rawWindowDays = getFormStringValue(formData, 'windowDays');
        const nextDuration = isAuditDuration(rawDuration) ? rawDuration : DEFAULT_DURATION;
        const normalizedFocusDateTime = rawFocusDateTime.trim();

        const nextSearchParams = new URLSearchParams();
        nextSearchParams.set('symbol', rawSymbol.trim().toUpperCase() || DEFAULT_SYMBOL);
        nextSearchParams.set('duration', nextDuration);
        nextSearchParams.set('endDateTime', rawEndDateTime.trim() || DEFAULT_END_DATE_TIME);
        nextSearchParams.set('windowDays', String(parsePositiveInteger(rawWindowDays, DEFAULT_WINDOW_DAYS)));
        nextSearchParams.set('shouldRemoveAfterHours', formData.get('shouldRemoveAfterHours') ? '1' : '0');
        nextSearchParams.set('showFocusedMarkersOnly', resolvedShowFocusedMarkersOnly ? '1' : '0');
        applySelectedEventTypesToSearchParams(nextSearchParams, resolvedSelectedEventTypes);

        if (normalizedFocusDateTime.length > 0) {
            nextSearchParams.set('focusDateTime', normalizedFocusDateTime);
        }

        setSearchParams(nextSearchParams);
    };

    const handlePresetSelect = (preset: AuditPreset) => {
        const nextSearchParams = new URLSearchParams();
        nextSearchParams.set('symbol', preset.symbol);
        nextSearchParams.set('duration', preset.duration);
        nextSearchParams.set('endDateTime', preset.endDateTime);
        nextSearchParams.set('windowDays', String(preset.windowDays));
        nextSearchParams.set('shouldRemoveAfterHours', '1');
        nextSearchParams.set('showFocusedMarkersOnly', '1');

        if (preset.focusDateTime.length > 0) {
            nextSearchParams.set('focusDateTime', preset.focusDateTime);
        }

        setSearchParams(nextSearchParams);
    };

    const handleFocusEvent = (dateTime: string) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('focusDateTime', dateTime);
        setSearchParams(nextSearchParams);
    };

    const handleFocusRelativeEvent = (offset: -1 | 1) => {
        if (selectedEventDateTimeIndex === -1) {
            return;
        }

        const nextDateTime = filteredEventDateTimes[selectedEventDateTimeIndex + offset];

        if (!nextDateTime) {
            return;
        }

        handleFocusEvent(nextDateTime);
    };

    const handleClearFocus = () => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('focusDateTime');
        setSearchParams(nextSearchParams);
    };

    const handleToggleEventType = (eventType: OBVAnalyzerEvent['type']) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        const nextSelectedEventTypes = new Set(resolvedSelectedEventTypes);

        if (nextSelectedEventTypes.has(eventType)) {
            if (nextSelectedEventTypes.size === 1) {
                return;
            }

            nextSelectedEventTypes.delete(eventType);
        } else {
            nextSelectedEventTypes.add(eventType);
        }

        applySelectedEventTypesToSearchParams(nextSearchParams, nextSelectedEventTypes);
        setSearchParams(nextSearchParams);
    };

    const handleSelectAllEventTypes = () => {
        const nextSearchParams = new URLSearchParams(searchParams);
        applySelectedEventTypesToSearchParams(nextSearchParams, new Set(ALL_OBV_EVENT_TYPES));
        setSearchParams(nextSearchParams);
    };

    const handleToggleFocusedMarkersOnly = () => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('showFocusedMarkersOnly', resolvedShowFocusedMarkersOnly ? '0' : '1');
        setSearchParams(nextSearchParams);
    };

    const activeError =
        symbolSearchQuery.error?.message ??
        entityQuery.error?.message ??
        auditQuery.error?.message ??
        (!resolvedAssetSearchResult && resolvedSymbol.length > 0 && !symbolSearchQuery.isLoading
            ? `No Asset found for symbol ${resolvedSymbol}`
            : null);

    return (
        <AdminLayout containerSize="2200px" title="OBV Audit">
            <div className="space-y-6">
                <Alert className="border border-zinc-800 bg-[#111111] text-zinc-100" variant="dark">
                    Use this page to complete the OBV manual review. Pick a known event or symbol/date, inspect the price chart with OBV
                    event markers, confirm the runtime state and focused-day screener snapshot, then have a trader or domain owner confirm
                    that the labels and semantics match how they would actually use OBV in live strategy design.
                </Alert>

                <AuditFiltersCard
                    formKey={searchParams.toString()}
                    resolvedDuration={resolvedDuration}
                    resolvedEndDateTime={resolvedEndDateTime}
                    resolvedFocusDateTime={resolvedFocusDateTime}
                    resolvedShouldRemoveAfterHours={resolvedShouldRemoveAfterHours}
                    resolvedSymbol={resolvedSymbol}
                    resolvedWindowDays={resolvedWindowDays}
                    onApplyFilters={handleApplyFilters}
                    onClearFocus={handleClearFocus}
                    onPresetSelect={handlePresetSelect}
                />

                {activeError ? <Alert variant="danger">{activeError}</Alert> : null}

                {symbolSearchQuery.isLoading || entityQuery.isLoading || auditQuery.isLoading ? (
                    <Card responsivePadding title="Loading OBV audit">
                        <DataLoadingSpinner text="Loading OBV audit data..." />
                    </Card>
                ) : null}

                {entity && auditQuery.data ? (
                    <>
                        <AuditSummaryCards
                            auditData={auditQuery.data}
                            entity={entity}
                            filteredEventCount={filteredEvents.length}
                            resolvedDuration={resolvedDuration}
                            selectedDateTime={selectedDateTime}
                        />

                        <div className="grid gap-6 xl:grid-cols-[1.7fr_0.9fr]">
                            <PriceChartCard
                                chartWindow={chartWindow}
                                entity={entity}
                                eventCounts={eventCounts}
                                filteredEventDateTimeCount={filteredEventDateTimes.length}
                                markers={markers}
                                resolvedDuration={resolvedDuration}
                                resolvedSelectedEventTypes={resolvedSelectedEventTypes}
                                resolvedShowFocusedMarkersOnly={resolvedShowFocusedMarkersOnly}
                                selectedDateTime={selectedDateTime}
                                selectedEventDateTimeIndex={selectedEventDateTimeIndex}
                                selectedEventMarkerLabelByKey={selectedEventMarkerLabelByKey}
                                selectedEvents={selectedEvents}
                                onFocusRelativeEvent={handleFocusRelativeEvent}
                                onSelectAllEventTypes={handleSelectAllEventTypes}
                                onToggleEventType={handleToggleEventType}
                                onToggleFocusedMarkersOnly={handleToggleFocusedMarkersOnly}
                            />

                            <ObvLineSignalCard
                                chartWindow={chartWindow}
                                obvChartMarkers={obvChartMarkers}
                                obvChartPoints={obvChartPoints}
                                selectedDateTime={selectedDateTime}
                            />

                            <ReviewerChecklistCard />

                            <AiAuditCard
                                isRunDisabled={!entity || auditQuery.isLoading}
                                isRunPending={runAiAuditMutation.isPending}
                                latestAiAuditResult={latestAiAuditResult}
                                renderUrls={renderUrls}
                                onRunAiAudit={handleRunAiAudit}
                            />
                        </div>

                        <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
                            <div className="space-y-6">
                                <FocusedRuntimeStateCard selectedState={selectedState} />

                                <FocusedDailySnapshotCard focusedDailySnapshot={auditQuery.data.focusedDailySnapshot} />
                            </div>

                            <div className="space-y-6">
                                <FocusedEventsCard selectedEvents={selectedEvents} />

                                <EventDistributionCard
                                    eventCounts={eventCounts}
                                    resolvedSelectedEventTypes={resolvedSelectedEventTypes}
                                    onToggleEventType={handleToggleEventType}
                                />

                                <FilteredEventTimelineCard
                                    filteredEvents={filteredEvents}
                                    selectedDateTime={selectedDateTime}
                                    onFocusEvent={handleFocusEvent}
                                />
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </AdminLayout>
    );
};

export default OBVAudit;
